import { FC, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useBlocker, useLocation, useSearchParams } from 'react-router-dom';

import { AsyncSearch, Loader } from 'components';
import Dialog, { useDialog } from 'components/organization/dialog/dialog';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import { useSelectedLocation } from 'contexts';
import { useResponse } from 'contexts/response';
import { useFleetFiles } from 'hooks';
import { useCustomerAPI } from 'hooks/use-customer-api';
import { Customer, FleetFile } from 'types';

import { LinkEdits, toRequest, viewOf, withEdit } from './link-edits';

/*
 * Bulk editor: which accounts each fleet customer code stands for at ONE location.
 *
 * The location is the editor's own, taken from its URL when it opens and changed only by an explicit
 * decision. The header's location picker writes the same `location` URL parameter, so a header change
 * is a navigation: with no pending edits it goes through and the editor follows the URL. With pending
 * edits every navigation away — header switch, cancel, a sidebar link, browser back — is held by a
 * router blocker until the admin answers, so the URL and the header don't change before then (F10).
 * Edits are keyed by account and saved all at once; the screen, not the hook, decides what happens
 * after a save (F09).
 */
const NewCustomerMapping: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { selectedLocation, locations } = useSelectedLocation();
  const { setError, setSuccess } = useResponse();
  const { searchCustomers } = useCustomerAPI();
  const { getCustomerLinks, putCustomerLinks } = useFleetFiles();

  const urlLocationId = Number(searchParams.get('location')) || selectedLocation?.id;
  const [locationId, setLocationId] = useState<number | undefined>(urlLocationId);
  const [links, setLinks] = useState<FleetFile.CustomerLink[]>([]);
  const [edits, setEdits] = useState<LinkEdits>(new Map());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sortByName, setSortByName] = useState(false);

  /* a ref, not state: two clicks in one frame both see `saving === false` in their closures */
  const savingRef = useRef(false);
  /*
   * Also a ref: the blocker reads it when a navigation starts, and a save or a discard clears it in the
   * same handler that navigates, before the render that would give the blocker a fresh closure.
   */
  const dirtyRef = useRef(false);
  dirtyRef.current = edits.size > 0;

  /*
   * This also holds navigation while a save is in flight, because the edits stay pending until it
   * resolves. Clearing them before the `await` would let a switch through mid-save.
   */
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirtyRef.current &&
      (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search)
  );
  /* destructured: the returned object is new every render, but these callbacks are stable */
  const { ref: leaveDialogRef, openDialog: openLeaveDialog, closeDialog: closeLeaveDialog } = useDialog();

  const nameOf = useCallback(
    (id?: number) => locations.find(l => l.id === id)?.name ?? (id ? `Location ${id}` : ''),
    [locations]
  );
  const locationName = useMemo(() => nameOf(locationId), [locationId, nameOf]);

  /* where the held navigation was going: another location of this editor, or somewhere else */
  const heldSwitchTo = useMemo(() => {
    if (blocker.state !== 'blocked' || blocker.location.pathname !== pathname) return undefined;
    return Number(new URLSearchParams(blocker.location.search).get('location')) || undefined;
  }, [blocker, pathname]);

  useEffect(() => {
    if (blocker.state === 'blocked') openLeaveDialog();
  }, [blocker.state, openLeaveDialog]);

  /* the blocker only sees in-app navigation; a reload or closing the tab gets the browser's own prompt */
  const dirty = edits.size > 0;
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  /* adopt a location once one is known, if the editor opened before the header had one */
  useEffect(() => {
    if (locationId === undefined && urlLocationId !== undefined) setLocationId(urlLocationId);
  }, [locationId, urlLocationId]);

  /* the URL moved to another location; with pending edits the blocker holds that until they're discarded */
  useEffect(() => {
    if (urlLocationId === undefined || locationId === undefined || urlLocationId === locationId) return;
    if (edits.size === 0) setLocationId(urlLocationId);
  }, [edits.size, locationId, urlLocationId]);

  /* load the editor's location; a response for a location it has since left is ignored */
  useEffect(() => {
    if (!locationId) return;
    let current = true;
    setLoading(true);
    setLinks([]);
    getCustomerLinks(locationId)
      .then(loaded => current && setLinks(loaded))
      .catch(e => current && setError(e instanceof Error ? e.message : 'Fleet file codes could not be loaded'))
      .finally(() => current && setLoading(false));
    return () => {
      current = false;
    };
  }, [getCustomerLinks, locationId, setError]);

  /* clear first, then let the held navigation through; the URL effect then loads the new location */
  const discardAndLeave = useCallback(() => {
    dirtyRef.current = false;
    setEdits(new Map());
    closeLeaveDialog();
    if (blocker.state === 'blocked') blocker.proceed();
  }, [blocker, closeLeaveDialog]);

  /* the held navigation is dropped, so the URL and the header never moved */
  const keepEditing = useCallback(() => {
    closeLeaveDialog();
    if (blocker.state === 'blocked') blocker.reset();
  }, [blocker, closeLeaveDialog]);

  const cards = useMemo(() => {
    const view = viewOf(links, edits);
    return sortByName ? [...view].sort((a, b) => a.name.localeCompare(b.name)) : view;
  }, [edits, links, sortByName]);

  const link = useCallback(
    (code: number) => (customer?: Customer) => {
      if (!customer) return;
      const { id, code: accountCode, name } = customer;
      setEdits(previous => withEdit(previous, links, { id, code: accountCode, name }, code));
    },
    [links]
  );

  const unlink = useCallback(
    (account: FleetFile.FleetAccount) => setEdits(previous => withEdit(previous, links, account, null)),
    [links]
  );

  /* a removal or a move, back to what the server holds: `withEdit` drops an edit that matches it */
  const undoEdit = useCallback(
    (account: FleetFile.FleetAccount, code: number) => setEdits(previous => withEdit(previous, links, account, code)),
    [links]
  );

  /* scoped to the editor's location, and not narrowed to the admin's own account associations */
  const searchAtLocation = useCallback(
    (input: string) => searchCustomers(input, false, locationId),
    [locationId, searchCustomers]
  );

  const handleReset = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      navigate(`/settings/fleet-files?location=${locationId ?? ''}`);
    },
    [navigate, locationId]
  );

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (savingRef.current || !locationId || edits.size === 0) return;
      savingRef.current = true;
      setSaving(true);
      try {
        const committed = await putCustomerLinks(locationId, toRequest(edits));
        setLinks(committed);
        dirtyRef.current = false;
        setEdits(new Map());
        setSuccess('Fleet file customer links saved');
        navigate(`/settings/fleet-files?location=${locationId}`);
      } catch (err) {
        /* F09: the edits stay, the reason is shown, and the admin can retry */
        setError(err instanceof Error ? err.message : 'Fleet file customer links could not be saved');
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
    [edits, locationId, navigate, putCustomerLinks, setError, setSuccess]
  );

  const formatBlank = useCallback(() => '', []);
  const formatOption = useCallback((customer: Customer) => customer.name, []);

  return (
    <form className="page" onReset={handleReset} onSubmit={handleSubmit}>
      <ErrorDialog title="Fleet file links not saved" />
      <Dialog closeDialog={keepEditing} ref={leaveDialogRef} data-cy="discard-fleet-edits-dialog">
        {/*
         * A save in flight can't be discarded: the request has reached the server, and cancelling the
         * fetch would not roll it back. So leaving waits for it (F10). If it succeeds the editor
         * navigates to the list and the held navigation is dropped; if it fails the edits are still
         * pending and this dialog becomes the discard prompt.
         */}
        {saving ? (
          <div className="d-flex flex-column g-3">
            <h3 className="text-info-dark font-weight-medium">Saving…</h3>
            <p className="text-info-main">
              Your fleet file changes for {locationName} are being saved. You can leave once the save finishes.
            </p>
            <div className="grid-3 mt-3">
              <button type="button" className="btn-outline col-start-3" onClick={keepEditing}>
                ok
              </button>
            </div>
          </div>
        ) : (
          <div className="d-flex flex-column g-3">
            <h3 className="text-info-dark font-weight-medium">Discard unsaved changes?</h3>
            <p className="text-info-main">
              You have {edits.size} unsaved fleet file change{edits.size === 1 ? '' : 's'} for {locationName}.{' '}
              {heldSwitchTo !== undefined && heldSwitchTo !== locationId
                ? `Switching to ${nameOf(heldSwitchTo)} discards them.`
                : 'Leaving this page discards them.'}
            </p>
            <div className="grid-3 mt-3">
              <button type="button" className="btn-outline col-start-2" onClick={keepEditing}>
                keep editing
              </button>
              <button type="button" className="btn-primary" onClick={discardAndLeave}>
                discard
              </button>
            </div>
          </div>
        )}
      </Dialog>
      <Loader loading={loading}>
        <div className="page-header">
          <h1>FLEET FILE CUSTOMER MAPPING{locationName ? ` — ${locationName.toUpperCase()}` : ''}</h1>
          <div className="d-flex align-items-center g-3">
            <span className="text-info-main text-base">SORT BY</span>
            <span className="text-info-dark cursor-pointer" onClick={() => setSortByName(false)}>
              CODE
            </span>
            <label className="switch">
              <input type="checkbox" checked={sortByName} onChange={e => setSortByName(e.target.checked)} />
              <span className="slider round" />
            </label>
            <span className="text-info-dark cursor-pointer" onClick={() => setSortByName(true)}>
              NAME
            </span>
          </div>
          <div className="grid-2">
            <button
              className="btn-outline text-danger"
              type="reset"
              style={{ borderColor: 'currentColor' }}
              disabled={saving}
            >
              cancel
            </button>
            <button className="btn-primary" type="submit" disabled={saving || edits.size === 0}>
              {saving ? 'saving…' : `submit${edits.size ? ` (${edits.size})` : ''}`}
            </button>
          </div>
        </div>
        <div className="page-content grid-auto">
          {cards.map(card => (
            <div key={card.code} className="card-primary" data-cy={`fleet-code-${card.code}`}>
              <div className="card-header">
                <h4>
                  {card.code}. {card.name}
                </h4>
              </div>
              <div className="card-body d-flex flex-column g-2">
                {card.customers.map(account => (
                  <div key={account.id} className="d-flex align-items-center justify-content-between g-2">
                    <span
                      className={account.pending ? 'font-weight-medium' : ''}
                      style={
                        account.removed || account.movingTo
                          ? { textDecoration: 'line-through', opacity: 0.6 }
                          : undefined
                      }
                    >
                      {account.name}
                      {!!account.movedFrom && (
                        <small className="text-muted">
                          {' '}
                          (moves from {account.movedFrom.code}. {account.movedFrom.name})
                        </small>
                      )}
                      {!!account.movingTo && (
                        <small className="text-muted">
                          {' '}
                          (moves to {account.movingTo.code}. {account.movingTo.name})
                        </small>
                      )}
                      {account.pending && !account.movedFrom && !account.movingTo && !account.removed && (
                        <small className="text-muted"> (new)</small>
                      )}
                    </span>
                    {account.removed || account.movingTo ? (
                      <button
                        type="button"
                        className="btn-outline"
                        aria-label={`Keep ${account.name} on ${card.code}`}
                        onClick={() => undoEdit(account, card.code)}
                        disabled={saving}
                      >
                        undo
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn-outline text-danger"
                        aria-label={`Remove ${account.name} from ${card.code}`}
                        onClick={() => unlink(account)}
                        disabled={saving}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <AsyncSearch<Customer>
                  formatValue={formatBlank}
                  formatOption={formatOption}
                  search={searchAtLocation}
                  selected={new Set(card.customers.map(c => c.id))}
                  handleSelect={link(card.code)}
                  disabled={saving}
                />
              </div>
            </div>
          ))}
        </div>
      </Loader>
    </form>
  );
};

export default NewCustomerMapping;
